import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Users, Hotel, Sparkles, DollarSign, ArrowLeft } from 'lucide-react';

const ItineraryDetailPage = () => {
  const { id } = useParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Mock data - in real app, fetch from API
  const itinerary = {
    'royal-rajasthan': {
      title: 'Royal Rajasthan Experience',
      destination: 'India',
      duration: '10 Days / 9 Nights',
      image: 'https://images.unsplash.com/photo-1770665567877-72ee8a7c9051?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHx1ZGFpcHVyJTIwbGFrZSUyMHBhbGFjZSUyMGx1eHVyeSUyMGhvdGVsJTIwaW5kaWF8ZW58MHx8fHwxNzczMTQ0NTU0fDA&ixlib=rb-4.1.0&q=85',
      description: 'Experience the grandeur of India\'s royal heritage through palace stays, private cultural immersions, and exclusive access to historical monuments.',
      priceGuidance: 'From $15,000 per person',
      dayByDay: [
        { day: 1, title: 'Arrival in Delhi', description: 'Private airport transfer to The Oberoi, New Delhi. Evening city orientation and welcome dinner.' },
        { day: 2, title: 'Delhi Heritage Tour', description: 'Private guided tour of Red Fort, Humayun\'s Tomb, and Qutub Minar with expert historian.' },
        { day: 3, title: 'Jaipur - The Pink City', description: 'Private jet to Jaipur. Check into Rambagh Palace. Evening folk dance performance.' },
        { day: 4, title: 'Jaipur Palaces', description: 'Private tour of Amber Fort via elephant. Visit City Palace and Jantar Mantar observatory.' },
        { day: 5, title: 'Udaipur - City of Lakes', description: 'Transfer to Udaipur. Check into Taj Lake Palace. Sunset boat ride on Lake Pichola.' },
        { day: 6, title: 'Udaipur Exploration', description: 'Visit City Palace Museum, Jagdish Temple. Private cooking class with royal chef.' },
        { day: 7, title: 'Jodhpur - The Blue City', description: 'Fly to Jodhpur. Stay at Umaid Bhawan Palace. Mehrangarh Fort private tour.' },
        { day: 8, title: 'Desert Experience', description: 'Private camel safari in Thar Desert. Luxury desert camp with traditional Rajasthani dinner.' },
        { day: 9, title: 'Agra & Taj Mahal', description: 'Early morning private Taj Mahal sunrise visit. Agra Fort tour. Return to Delhi.' },
        { day: 10, title: 'Departure', description: 'Leisure morning. Private transfer to airport for international departure.' },
      ],
      hotels: [
        'The Oberoi, New Delhi',
        'Rambagh Palace, Jaipur',
        'Taj Lake Palace, Udaipur',
        'Umaid Bhawan Palace, Jodhpur',
      ],
      experiences: [
        'Private historian-led heritage tours',
        'Exclusive palace access after hours',
        'Traditional Rajasthani cooking class',
        'Desert camping under the stars',
        'Sunrise Taj Mahal private visit',
        'Royal folk performances',
      ],
    },
  }[id] || {};

  if (!itinerary.title) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-primary text-lg">Itinerary not found</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="itinerary-detail-page"
    >
      {/* Hero */}
      <section className="relative h-[70vh] flex items-end overflow-hidden" data-testid="itinerary-detail-hero">
        <div className="absolute inset-0">
          <img
            src={itinerary.image}
            alt={itinerary.title}
            className="hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        </div>

        <div className="relative z-10 container-padding pb-16 max-w-[1920px] mx-auto w-full">
          <Link
            to="/itineraries"
            className="inline-flex items-center gap-2 text-primary/80 hover:text-primary text-sm mb-8 transition-colors duration-300"
            data-testid="back-to-itineraries-link"
          >
            <ArrowLeft size={16} /> Back to Itineraries
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <div className="flex items-center gap-4 text-muted-foreground text-sm mb-4">
              <div className="flex items-center gap-2">
                <MapPin size={16} className="text-accent" />
                <span>{itinerary.destination}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-accent" />
                <span>{itinerary.duration}</span>
              </div>
            </div>

            <h1 className="font-heading text-5xl md:text-7xl font-light tracking-tight text-primary mb-6" data-testid="itinerary-detail-title">
              {itinerary.title}
            </h1>

            <p className="text-muted-foreground text-lg max-w-3xl">
              {itinerary.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Price & Quick Info */}
      <section className="section-padding container-padding bg-white/[0.02]" data-testid="itinerary-quick-info">
        <div className="max-w-[1920px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="glass-card p-8 text-center">
              <DollarSign className="text-accent mx-auto mb-4" size={40} />
              <h3 className="font-heading text-2xl font-light text-primary mb-2">Price Guidance</h3>
              <p className="text-accent text-lg font-medium">{itinerary.priceGuidance}</p>
            </div>
            <div className="glass-card p-8 text-center">
              <Calendar className="text-accent mx-auto mb-4" size={40} />
              <h3 className="font-heading text-2xl font-light text-primary mb-2">Duration</h3>
              <p className="text-muted-foreground">{itinerary.duration}</p>
            </div>
            <div className="glass-card p-8 text-center">
              <MapPin className="text-accent mx-auto mb-4" size={40} />
              <h3 className="font-heading text-2xl font-light text-primary mb-2">Destination</h3>
              <p className="text-muted-foreground">{itinerary.destination}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Day by Day Itinerary */}
      <section className="section-padding container-padding" data-testid="itinerary-day-by-day">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <p className="font-body text-xs uppercase tracking-[0.3em] text-accent mb-4">Journey</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light text-primary">Day by Day</h2>
          </div>

          <div className="space-y-12">
            {itinerary.dayByDay.map((item, index) => (
              <motion.div
                key={item.day}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05, duration: 0.5 }}
                className="flex gap-6 group"
                data-testid={`day-${item.day}`}
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full border border-accent flex items-center justify-center text-accent font-body text-sm group-hover:bg-accent group-hover:text-background transition-all duration-300">
                    {item.day}
                  </div>
                </div>
                <div>
                  <h3 className="font-heading text-2xl font-light text-primary mb-2">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Hotels */}
      <section className="section-padding container-padding bg-white/[0.02]" data-testid="itinerary-hotels">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Hotel className="text-accent mx-auto mb-4" size={48} />
            <h2 className="font-heading text-4xl md:text-5xl font-light text-primary mb-4">
              Featured Hotels
            </h2>
          </div>

          <ul className="space-y-4">
            {itinerary.hotels.map((hotel, index) => (
              <motion.li
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="text-muted-foreground text-lg border-b border-white/5 pb-4"
                data-testid={`hotel-${index}`}
              >
                {hotel}
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* Signature Experiences */}
      <section className="section-padding container-padding" data-testid="itinerary-experiences">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Sparkles className="text-accent mx-auto mb-4" size={48} />
            <h2 className="font-heading text-4xl md:text-5xl font-light text-primary mb-4">
              Signature Experiences
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {itinerary.experiences.map((experience, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="glass-card p-6"
                data-testid={`experience-${index}`}
              >
                <p className="text-muted-foreground">{experience}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding container-padding bg-white/[0.02] text-center" data-testid="itinerary-cta">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mx-auto"
        >
          <h2 className="font-heading text-4xl md:text-5xl font-light text-primary mb-6">
            Customize This Journey
          </h2>
          <p className="text-muted-foreground text-lg mb-10">
            This is a sample itinerary. We\'ll craft a completely bespoke version tailored to your
            preferences, dates, and desires.
          </p>
          <Link to="/concierge" className="btn-primary" data-testid="customize-journey-btn">
            Start Planning Your Journey
          </Link>
        </motion.div>
      </section>
    </motion.div>
  );
};

export default ItineraryDetailPage;